import { CHAT_MIN_WIDTH, MODAL_WITH_SIDE_PANEL_MIN_WIDTH, VIEWPORT_MARGIN } from './consts';
import type { DragStart, ModalPosition, ModalSize, ModalViewport, ResizeConstraints, ResizeStart } from './types';

type PointerCoords = { clientX: number; clientY: number };

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const getViewportBounds = (width: number, height: number) => {
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

  return {
    minX: VIEWPORT_MARGIN,
    maxX: Math.max(VIEWPORT_MARGIN, windowWidth - width - VIEWPORT_MARGIN),
    minY: VIEWPORT_MARGIN,
    maxY: Math.max(VIEWPORT_MARGIN, windowHeight - height - VIEWPORT_MARGIN),
  };
};

// Shrinks the size to fit inside the viewport (minus VIEWPORT_MARGIN on each side).
export const fitSizeToViewport = (size: ModalSize): ModalSize => {
  if (typeof window === 'undefined') return size;

  const maxWidth = Math.max(1, window.innerWidth - VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(1, window.innerHeight - VIEWPORT_MARGIN * 2);

  return {
    width: Math.min(size.width, maxWidth),
    height: Math.min(size.height, maxHeight),
  };
};

export const calculateDefaultPosition = (width: number, height: number): ModalPosition => {
  if (typeof window === 'undefined') {
    return { x: 80, y: 80 };
  }

  const x = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
  const y = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);

  return { x, y };
};

// Ensures the modal (position + size) stays inside the viewport after a window resize.
export const fitViewportToWindow = (
  position: ModalPosition,
  size: ModalSize
): { position: ModalPosition; size: ModalSize } => {
  const nextSize = fitSizeToViewport(size);
  const { minX, maxX, minY, maxY } = getViewportBounds(nextSize.width, nextSize.height);

  return {
    size: nextSize,
    position: {
      x: clamp(position.x, minX, maxX),
      y: clamp(position.y, minY, maxY),
    },
  };
};

// Resizes to the chat-only width, or grows to fit the side panel, anchoring the right edge.
export const fitViewportToSidePanel = ({ position, size }: ModalViewport, hasSidePanel: boolean): ModalViewport => {
  const targetWidth = hasSidePanel
    ? Math.max(size.width, MODAL_WITH_SIDE_PANEL_MIN_WIDTH)
    : size.width || CHAT_MIN_WIDTH;
  const rightEdge = position.x + size.width;

  return fitViewportToWindow(
    { x: rightEdge - targetWidth, y: position.y },
    { width: targetWidth, height: size.height }
  );
};

export const computeDragPosition = (dragStart: DragStart, event: PointerCoords, size: ModalSize): ModalPosition => {
  const deltaX = event.clientX - dragStart.mouseX;
  const deltaY = event.clientY - dragStart.mouseY;
  const { minX, maxX, minY, maxY } = getViewportBounds(size.width, size.height);

  return {
    x: clamp(dragStart.posX + deltaX, minX, maxX),
    y: clamp(dragStart.posY + deltaY, minY, maxY),
  };
};

export const computeResizeViewport = (
  resizeStart: ResizeStart,
  event: PointerCoords,
  { minWidth, minHeight, maxWidth, maxHeight }: ResizeConstraints
): { position: ModalPosition; size: ModalSize } => {
  const deltaX = event.clientX - resizeStart.mouseX;
  const deltaY = event.clientY - resizeStart.mouseY;

  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 2000;
  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 2000;

  const maxW = maxWidth ?? windowWidth;
  const maxH = maxHeight ?? windowHeight;

  let newWidth = resizeStart.width;
  let newHeight = resizeStart.height;
  let newX = resizeStart.posX;
  let newY = resizeStart.posY;

  const dir = resizeStart.direction;

  if (dir.includes('right')) {
    newWidth = clamp(
      resizeStart.width + deltaX,
      minWidth,
      Math.min(maxW, windowWidth - resizeStart.posX - VIEWPORT_MARGIN)
    );
  } else if (dir.includes('left')) {
    const nextLeft = clamp(resizeStart.posX + deltaX, VIEWPORT_MARGIN, resizeStart.posX + resizeStart.width - minWidth);
    newX = nextLeft;
    newWidth = resizeStart.width - (nextLeft - resizeStart.posX);
  }

  if (dir.includes('bottom')) {
    newHeight = clamp(
      resizeStart.height + deltaY,
      minHeight,
      Math.min(maxH, windowHeight - resizeStart.posY - VIEWPORT_MARGIN)
    );
  } else if (dir.includes('top')) {
    const nextTop = clamp(
      resizeStart.posY + deltaY,
      VIEWPORT_MARGIN,
      resizeStart.posY + resizeStart.height - minHeight
    );
    newY = nextTop;
    newHeight = resizeStart.height - (nextTop - resizeStart.posY);
  }

  newX = clamp(newX, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, windowWidth - newWidth - VIEWPORT_MARGIN));
  newY = clamp(newY, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, windowHeight - newHeight - VIEWPORT_MARGIN));

  return {
    size: { width: newWidth, height: newHeight },
    position: { x: newX, y: newY },
  };
};
